import { useCallback, useRef } from 'react';
import { v4 as uuid } from 'uuid';

import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useMSRNudgeRecommendations } from '../recommendations/index.tsx';
import { getFeatureVisited } from '../caching/index.tsx';
import { fireErrorAnalytics } from '../analytics/index.tsx';
import { useJpdMsrCrossJoinNudge } from '../../ui/nudges/jpd-msr-nudge.tsx';
import { useOverrides } from '../overrides/index.tsx';
import { useMsrConfluenceCrossJoinNudge } from '../../ui/nudges/confluence-msr-nudge.tsx';
import { useMsrJpdCrossJoinNudgeEligibility } from './jpd-eligibility.tsx';
import { useMsrConfluenceCrossJoinNudgeEligibility } from './confluence-eligibility.tsx';
import { useMsrSnoozeHandler } from './snooze-handler.tsx';

/*
  Eligibility can be overriden using the following query parameter:

  ?cjn_msr_overrides={
    "feature_visited_too_recently": false,
  }
*/
type OverrideKey = 'feature_visited_too_recently';

const RECOMMENDATION_PRODUCT_KEYS = {
	JPD: 'cjn-jira-jpd',
	CONFLUENCE: 'cjn-jira-conf-v2',
} as const;

export const useExecuteMSRNudge = () => {
	const sessionId = useRef(uuid());
	const cloudId = useCloudId();
	const getMSRNudgeRecommendations = useMSRNudgeRecommendations({ sessionId: sessionId.current });
	const getMsrJpdCrossJoinNudgeEligibility = useMsrJpdCrossJoinNudgeEligibility();
	const getMsrConfluenceCrossJoinNudgeEligibility = useMsrConfluenceCrossJoinNudgeEligibility();
	const { blockEligibilityUntilTomorrow, snooze } = useMsrSnoozeHandler();
	const showJpdMsrCrossJoinNudge = useJpdMsrCrossJoinNudge();
	const showMsrConfluenceCrossJoinNudge = useMsrConfluenceCrossJoinNudge();
	const withOverrides = useOverrides<OverrideKey>('cjn_msr_overrides');

	return useCallback(async () => {
		try {
			if (withOverrides('feature_visited_too_recently', getFeatureVisited('crossJoinNudgesMsr'))) {
				return;
			}

			const [jpdResult, confluenceResult] = await Promise.all([
				getMsrJpdCrossJoinNudgeEligibility(),
				getMsrConfluenceCrossJoinNudgeEligibility(),
			]);

			const rerankList = [];

			if (jpdResult.isEligible) {
				rerankList.push(RECOMMENDATION_PRODUCT_KEYS.JPD);
			}
			if (confluenceResult.isEligible) {
				rerankList.push(RECOMMENDATION_PRODUCT_KEYS.CONFLUENCE);
			}

			if (rerankList.length === 0) {
				snooze();
				return;
			}

			const recommendation = await getMSRNudgeRecommendations(rerankList);

			if (!recommendation) {
				blockEligibilityUntilTomorrow();
				return;
			}

			switch (recommendation) {
				case RECOMMENDATION_PRODUCT_KEYS.JPD:
					showJpdMsrCrossJoinNudge();
					break;
				case RECOMMENDATION_PRODUCT_KEYS.CONFLUENCE:
					showMsrConfluenceCrossJoinNudge();
					break;
				default:
			}
			snooze();

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('executeMSRNudge', error, { cloudId });
			// TODO: up to discussion if we should snooze here
			snooze();
		}
	}, [
		cloudId,
		getMsrJpdCrossJoinNudgeEligibility,
		getMsrConfluenceCrossJoinNudgeEligibility,
		getMSRNudgeRecommendations,
		blockEligibilityUntilTomorrow,
		showJpdMsrCrossJoinNudge,
		showMsrConfluenceCrossJoinNudge,
		withOverrides,
		snooze,
	]);
};
